import * as signalR from '@microsoft/signalr';
import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
} from 'react';
import { useUserContext } from '../user_context/UserProvider';
import { getOrganization } from '../../repository/OrganizationDAO';
import {
  invokeOrganizationSRGroup,
  negotiateSignalR,
} from '../../repository/SignalRDAO';
import { useLoading } from '../loading_context/LoadingContext';
import SignalROnFunctions from '../../utils/signal_r/SignalROnFunctions';
import ErrorComponent from '../../components/utils/ErrorComponent';
import { useConnectivity } from '../connectivity_context/ConnectivityProvider';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingNavBar from '../../components/navbar/LoadingNavBar';
import SignalRErrorCodes from '../../utils/error_codes/SignalRErrorCodes';
import { Organization } from '@/models/Organization/Organization';
import { getAllPvdConfigurations } from '@/repository/PvdConfigDAO';
import { getAllEmergencyConfigurations } from '@/repository/EmergencyConfigDAO';

export const SafeguardAdminSignalRContext = createContext();

/**
 * This component controls the SignalR connection for a Safeguard permissioned user
 */

/**
 * @typedef {Object} SafeguardAdminSignalRContext
 * @property {(orgCode: string) => void} setOrgCode - Function to set the organization code
 * @property {import('../../../generated-client/models').OrganizationOnAzure} organization - The current organization data
 * @property {signalR.HubConnection} connection - The SignalR connection instance
 * @property {Array<import('@/../generated-client/models').PvdConfigurationOnAzure>} pvdConfigurations
 * @property {Array<import('@/../generated-client/models').EmergencyConfigurationOnAzure>} emergencyConfigurations
 */

export const SafeguardAdminSignalRListener = ({ children }) => {
  const {
    bearerToken,
    isSafeguardAdmin,
    safeguardView,
    isSafeguardManufacturingUser,
    isSafeguardDev,
  } = useUserContext();
  const [orgCode, setOrgCode] = useState(null);
  /** @type {[import('../../../generated-client/models').OrganizationOnAzure | undefined, React.Dispatch<React.SetStateAction<import('../../../generated-client/models').OrganizationOnAzure | undefined>>]} */
  const [organization, setOrganization] = useState();
  const [pastOrgCode, setPastOrgCode] = useState(null);
  const [connection, setConnection] = useState();
  const { setSafeguardUserSignalrStatus } = useLoading();
  const { isOnline, isOnlineRef } = useConnectivity();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const [signalRNegotiateError, setSignalRNegotiateError] = useState(false);
  const [shouldRetry, setShouldRetry] = useState(false);
  const [lastConnectionAttempt, setLastConnectionAttempt] = useState(0);
  const [pvdConfigurations, setPvdConfigurations] = useState([]);
  const [emergencyConfigurations, setEmergencyConfigurations] = useState([]);
  const [configurationError, setConfigurationError] = useState(false);
  const CONNECTION_ATTEMPT_INTERVAL = 10000;

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
      return;
    }
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  // Build a new connection
  useEffect(() => {
    if (bearerToken && isOnlineRef.current) {
      setConnection();
      buildConnection();
      setShouldRetry(false);
    }
  }, [bearerToken, isOnlineRef, shouldRetry]);

  useEffect(() => {
    if (bearerToken && connection && orgCode && pastOrgCode !== orgCode) {
      if (pastOrgCode)
        updateOrganizationSRGroup(bearerToken, false, pastOrgCode);
      setPastOrgCode(orgCode);
      updateOrganizationSRGroup(bearerToken, true, orgCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearerToken, orgCode, pastOrgCode]);

  useEffect(() => {
    if (
      connection &&
      connection.state === signalR.HubConnectionState.Disconnected
    ) {
      if (
        (isSafeguardAdmin ||
          safeguardView ||
          isSafeguardManufacturingUser ||
          isSafeguardDev) &&
        isOnlineRef.current
      )
        startConnection();
      else if (!safeguardView) stopConnection();
    }
  }, [
    isSafeguardAdmin,
    connection,
    safeguardView,
    isSafeguardManufacturingUser,
    isSafeguardDev,
    isOnline,
  ]);

  const loadPvdConfigs = useCallback(async () => {
    if (!organization?.org_code) return;
    try {
      const configs = await getAllPvdConfigurations(organization.org_code);
      setPvdConfigurations(configs);
    } catch (error) { setConfigurationError(true); }
  }, [organization, bearerToken]);

  const loadEmergencyConfigs = useCallback(async () => {
    if (!organization?.org_code) return;
    try {
      const configs = await getAllEmergencyConfigurations(organization.org_code);
      setEmergencyConfigurations(configs);
    } catch (error) { setConfigurationError(true); }
  }, [organization, bearerToken]);

  useEffect(() => {
    if (isSafeguardDev) {
      loadPvdConfigs();
      loadEmergencyConfigs();
    }
  }, [loadPvdConfigs, loadEmergencyConfigs, isSafeguardDev]);

  const buildConnection = async () => {
    setConnection();
    const now = Date.now();
    if (now - lastConnectionAttempt < CONNECTION_ATTEMPT_INTERVAL) return;
    setLastConnectionAttempt(now);
    try {
      const signalRNegotiateResponse = await negotiateSignalR(bearerToken);
      if (!signalRNegotiateResponse.ok) {
        setSignalRNegotiateError(true);
        return;
      }

      const signalRData = await signalRNegotiateResponse.json();

      // Override URL if demo mode is enabled and demo URL is provided
      if (
        import.meta.env.VITE_USE_DEMO_MODE === 'true' &&
        import.meta.env.VITE_DEMO_MODE_SIGNAL_R_HUB_URL
      ) {
        signalRData.Url = import.meta.env.VITE_DEMO_MODE_SIGNAL_R_HUB_URL;
        console.log('Using demo mode SignalR URL:', signalRData.Url);
      }

      if (
        !connection ||
        connection.state === signalR.HubConnectionState.Disconnected
      ) {
        const newConnection = new signalR.HubConnectionBuilder()
          .withUrl(`${signalRData.Url}`, {
            accessTokenFactory: () => `${signalRData.AccessToken}`,
          })
          .withAutomaticReconnect([0, 2000, 10000, 30000])
          .build();

        setConnection(newConnection);
        setSignalRNegotiateError(false);
      }
    } catch (error) {
      setSignalRNegotiateError(true);
    }
  };

  const startConnection = async () => {
    if (
      isOnlineRef.current &&
      (isSafeguardAdmin || isSafeguardManufacturingUser || isSafeguardDev) &&
      safeguardView
    ) {
      try {
        connection.on(SignalROnFunctions.RECEIVE_ORGANIZATION, (data) => {
          setOrganization(data);
        });

        connection.on(SignalROnFunctions.RECEIVE_PVD_CONFIG_CHANGED, () => {
          loadPvdConfigs();
        });

        connection.on(SignalROnFunctions.RECEIVE_EMERGENCY_CONFIG_CHANGED, () => {
          loadEmergencyConfigs();
        });

        connection.onreconnected(async () => {
          setSafeguardUserSignalrStatus(connection.state);
        });
        connection.onreconnecting(() => {
          setSafeguardUserSignalrStatus(connection.state);
        });
        connection.onclose(async () => {
          setShouldRetry(true);
          setSafeguardUserSignalrStatus(connection.state);
          if (
            (isSafeguardAdmin ||
              isSafeguardManufacturingUser ||
              isSafeguardDev) &&
            isOnlineRef.current &&
            safeguardView
          ) {
            setTimeout(async () => {
              await startConnection();
            }, 5000);
          }
        });
        try {
          await connection.start();
        } catch (startError) {
          setConnection();
          setShouldRetry(true);
          return;
        } finally {
          setSafeguardUserSignalrStatus(connection.state);
        }
      } catch (err) {
        setShouldRetry(true);
      }
    }
  };

  const stopConnection = async () => {
    try {
      if (connection) {
        if (connection.state === signalR.HubConnectionState.Connected)
          await connection.stop();
      }
    } catch (error) {
      console.error('Error disconnecting SignalR:', error);
    }
  };

  const updateOrganizationSRGroup = async (bearerToken, join, orgCode) => {
    try {
      await invokeOrganizationSRGroup(
        join,
        connection?.connection?.connectionId,
        orgCode,
        bearerToken,
      ).then(async () => {
        /* connection to organization completed */
        if (join) {
          try {
            let organization = await getOrganization(orgCode);

            setOrganization(organization);
          } catch (error) {
            console.warn('Error fetching organization');
            console.error(error);
          }
        }
      });
    } catch (error) {
      console.error('Error updating organization SignalR group:', error);
    }
  };

  if (signalRNegotiateError) {
    return (
      <>
        <LoadingNavBar />
        <ErrorComponent error={SignalRErrorCodes.SIGNALR_NEGOTIATE_ERROR} />
      </>
    );
  }

  return (
    <SafeguardAdminSignalRContext.Provider
      value={{ setOrgCode, organization, connection, pvdConfigurations, emergencyConfigurations }}
    >
      {children}
    </SafeguardAdminSignalRContext.Provider>
  );
};

/**
 * @typedef {Object} SafeguardAdminSignalRContext
 * @property {(orgCode: string) => void} setOrgCode - Function to set the organization code
 * @property {Organization} organization - The current organization data
 * @property {signalR.HubConnection} connection - The SignalR connection instance
 * @property {Array<import('@/../generated-client/models').PvdConfigurationOnAzure>} pvdConfigurations
 * @property {Array<import('@/../generated-client/models').EmergencyConfigurationOnAzure>} emergencyConfigurations
 */

/** @returns {SafeguardAdminSignalRContext} */
export const useSafeguardAdminSignalRContext = () => {
  return useContext(SafeguardAdminSignalRContext);
};
